import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Command Palette extension point.
 *
 * Pages register additional command groups via `usePaletteCommands`
 * and unregister them on unmount. The palette UI itself lives in
 * `<GlobalCommandPalette>` and reads from the context's `groups`
 * array, rendering them in priority order.
 *
 * A `group` shape:
 *   {
 *     key: 'estimator-components',  // unique
 *     heading: 'Components — schedule overrides',
 *     priority: 50,                 // lower = earlier; built-in nav is 0/10
 *     items: [
 *       {
 *         id: 'comp-design-discovery',
 *         value: 'Design / Discovery Consulting CS',  // search corpus
 *         onSelect: () => jumpToComponent(...),
 *         render: () => <CustomNode/>  // optional override of the row
 *       }
 *     ]
 *   }
 */

const CommandPaletteContext = createContext(null);

export function CommandPaletteProvider({ children }) {
  const [open, setOpen] = useState(false);
  // Use a Map<key, group> ref so writes don't trigger re-renders of pages
  // calling `register` mid-render. We expose a serialized array via state
  // so the palette UI can re-render when groups change.
  const groupsRef = useRef(new Map());
  const [groupsVersion, setGroupsVersion] = useState(0);

  // ── Two-step / drill-down command stack ─────────────────────
  // Top-level commands can call pushStep({ label, placeholder, items |
  // loadItems }) to swap the palette into a sub-picker (e.g., "New
  // estimate for…" → org picker). The breadcrumb in the dialog header
  // shows the trail; Esc / Backspace pops one level off.
  const [stepStack, setStepStack] = useState([]);
  const pushStep = useCallback((step) => {
    setStepStack((prev) => [...prev, step]);
  }, []);
  const popStep = useCallback(() => {
    setStepStack((prev) => prev.slice(0, -1));
  }, []);
  const resetSteps = useCallback(() => setStepStack([]), []);

  // Auto-reset the step stack whenever the palette closes so the user
  // always re-enters at the root level.
  useEffect(() => {
    if (!open) setStepStack([]);
  }, [open]);

  const register = useCallback((key, group) => {
    groupsRef.current.set(key, { key, priority: 100, ...group });
    setGroupsVersion((v) => v + 1);
  }, []);

  const unregister = useCallback((key) => {
    if (groupsRef.current.delete(key)) {
      setGroupsVersion((v) => v + 1);
    }
  }, []);

  const groups = useMemo(() => {
    // Re-derive whenever groupsVersion ticks. groupsVersion is intentionally
    // referenced so the linter knows this memo depends on it.
    void groupsVersion;
    return Array.from(groupsRef.current.values()).sort((a, b) => a.priority - b.priority);
  }, [groupsVersion]);

  const value = useMemo(() => ({
    open, setOpen,
    register, unregister,
    groups,
    stepStack, pushStep, popStep, resetSteps,
    currentStep: stepStack[stepStack.length - 1] || null,
  }), [open, register, unregister, groups, stepStack, pushStep, popStep, resetSteps]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  return ctx;
}

/**
 * Convenience hook for pages to register a command group.
 * The group is unregistered automatically on unmount or when `enabled`
 * flips to false.
 *
 * Pass a `deps` array to recompute the group when relevant state changes
 * (e.g., when components / overrides change). Group payload is built
 * lazily via `factory()` so the caller can capture state in the closure
 * without needing memoization on every individual field.
 */
export function usePaletteCommands(key, factory, { enabled = true, deps = [] } = {}) {
  const { register, unregister } = useCommandPalette();
  useEffect(() => {
    if (!enabled) {
      unregister(key);
      return undefined;
    }
    const group = factory();
    if (group) register(key, group);
    return () => unregister(key);
    // factory is intentionally not in the dep list — callers control
    // when to refresh via the explicit `deps` array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, register, unregister, ...deps]);
}
